import { getIcon } from "@/constants";
import { useAppSelector } from "@/redux/hooks";
import { PageProps } from "@/types";
import { Link, usePage } from "@inertiajs/react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, LockKeyhole, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ReactSVG } from "react-svg";

/**
 * EquivalentRateHero — gated marketing panel for the new equivalent-rate
 * (Dream University) calculator. Shown to guests in place of the live
 * calculator; once signed in, the home page renders DreamUniversitySection
 * here instead. Drenched-teal surface so it stands out from the white
 * homepage cards and reads as a premium, unlockable feature.
 */

const UNIVERSITIES: { key: string; emoji: string }[] = [
    { key: "dream_university_uni_kuwait", emoji: "🏛️" },
    { key: "dream_university_uni_aasu", emoji: "🎓" },
    { key: "dream_university_uni_foreign_missions", emoji: "🌍" },
    { key: "dream_university_uni_internal_missions", emoji: "🏠" },
    { key: "dream_university_uni_paaet", emoji: "⚙️" },
];

const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

const rise = {
    hidden: { opacity: 0, y: 18 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
    },
};

export default function EquivalentRateHero() {
    const { t } = useTranslation();
    const { currentLang } = usePage<PageProps>().props;
    const { locale } = useAppSelector((state) => state);
    const DirArrow = locale.isRTL ? ArrowLeft : ArrowRight;

    return (
        <section className="relative isolate overflow-hidden rounded-3xl bg-theme-900 px-6 py-14 sm:px-10 sm:py-20 lg:px-16">
            {/* Ambient depth: layered teal/orange glows + faint dot field */}
            <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
                <div className="absolute inset-0 bg-gradient-to-br from-theme-800 via-theme-900 to-[#072e27]" />
                <motion.div
                    className="absolute -top-24 end-[-10%] h-[28rem] w-[28rem] rounded-full bg-theme-500/25 blur-3xl"
                    animate={{ y: [0, 24, 0], opacity: [0.5, 0.75, 0.5] }}
                    transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="absolute bottom-[-20%] start-[-8%] h-96 w-96 rounded-full bg-orange-500/20 blur-3xl"
                    animate={{ y: [0, -20, 0], opacity: [0.4, 0.65, 0.4] }}
                    transition={{ duration: 13, repeat: Infinity, ease: "easeInOut" }}
                />
                <div
                    className="absolute inset-0 opacity-[0.18]"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle, rgba(255,255,255,0.7) 1px, transparent 1px)",
                        backgroundSize: "26px 26px",
                        maskImage:
                            "radial-gradient(ellipse 75% 65% at 50% 35%, #000 30%, transparent 75%)",
                        WebkitMaskImage:
                            "radial-gradient(ellipse 75% 65% at 50% 35%, #000 30%, transparent 75%)",
                    }}
                />
            </div>

            <motion.div
                variants={container}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                className="mx-auto flex max-w-3xl flex-col items-center text-center"
            >
                {/* New-feature eyebrow */}
                <motion.span
                    variants={rise}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-sm font-semibold text-theme-100"
                >
                    <Sparkles className="h-4 w-4 text-orange-300" />
                    {t("eqr_hero_badge")}
                </motion.span>

                {/* Headline: solid white with one orange accent line */}
                <motion.h2
                    variants={rise}
                    className="mt-6 text-4xl font-extrabold leading-[1.18] tracking-tight text-white sm:text-5xl lg:text-6xl"
                >
                    {t("eqr_hero_title_1")}
                    <span className="mt-2 block text-orange-300">
                        {t("eqr_hero_title_2")}
                    </span>
                </motion.h2>

                <motion.div variants={rise}>
                    <ReactSVG
                        src={getIcon("title_arrow.svg")}
                        className="relative mx-auto mt-5 w-24 text-orange-300 ltr:rotate-180"
                    />
                </motion.div>

                <motion.p
                    variants={rise}
                    className="mt-6 max-w-2xl text-base leading-8 text-theme-100/90 sm:text-lg"
                >
                    {t("eqr_hero_subtitle")}
                </motion.p>

                {/* What the calculator covers */}
                <motion.div variants={rise} className="mt-9 w-full">
                    <p className="text-sm font-medium text-theme-100/70">
                        {t("eqr_hero_universities_label")}
                    </p>
                    <ul className="mt-4 flex flex-wrap items-center justify-center gap-2.5">
                        {UNIVERSITIES.map((u) => (
                            <li
                                key={u.key}
                                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.07] px-4 py-2 text-sm font-medium text-white transition-colors hover:border-white/25 hover:bg-white/15"
                            >
                                <span aria-hidden className="text-base leading-none">
                                    {u.emoji}
                                </span>
                                {t(u.key)}
                            </li>
                        ))}
                    </ul>
                </motion.div>

                {/* Dual CTAs: register + login, side by side */}
                <motion.div
                    variants={rise}
                    className="mt-10 flex w-full flex-col items-center justify-center gap-3 sm:flex-row"
                >
                    <Link
                        href={route(`${currentLang}.register`)}
                        className="group inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-8 py-4 text-base font-bold text-theme-800 shadow-lg shadow-theme-950/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-orange-300 hover:text-theme-900 sm:w-auto"
                    >
                        {t("eqr_hero_register")}
                        <DirArrow className="h-5 w-5 transition-transform duration-300 group-hover:rtl:-translate-x-1 group-hover:ltr:translate-x-1" />
                    </Link>
                    <Link
                        href={route(`${currentLang}.login`)}
                        className="inline-flex w-full items-center justify-center rounded-2xl border border-white/25 px-8 py-4 text-base font-semibold text-white transition-colors duration-300 hover:bg-white/10 sm:w-auto"
                    >
                        {t("eqr_hero_login")}
                    </Link>
                </motion.div>

                {/* Gated-access helper */}
                <motion.p
                    variants={rise}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm text-theme-100/70"
                >
                    <LockKeyhole className="h-3.5 w-3.5" />
                    {t("eqr_hero_unlock")}
                </motion.p>
            </motion.div>
        </section>
    );
}
